import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@platform/api";

export interface RFPConnectedCarrierResponse {
  name: string;
  salesRepEmails: string[];
}

type RawConnectedCarrierResponse = string | RFPConnectedCarrierResponse;

interface RawRFPConnectedCarriersResponse {
  connected: RawConnectedCarrierResponse[];
  unconnected: RawConnectedCarrierResponse[];
  unknown: RawConnectedCarrierResponse[];
  centroSelectCarrierEnabled: boolean;
}

interface RFPConnectedCarriersResponse {
  connected: RFPConnectedCarrierResponse[];
  unconnected: RFPConnectedCarrierResponse[];
  unknown: RFPConnectedCarrierResponse[];
  centroSelectCarrierEnabled: boolean;
}

function normalizeCarrier(
  carrier: RawConnectedCarrierResponse,
): RFPConnectedCarrierResponse {
  return typeof carrier === "string"
    ? { name: carrier, salesRepEmails: [] }
    : { name: carrier.name, salesRepEmails: carrier.salesRepEmails ?? [] };
}

function normalizeConnectedCarriersResponse(
  response: RawRFPConnectedCarriersResponse,
): RFPConnectedCarriersResponse {
  return {
    connected: (response.connected ?? []).map(normalizeCarrier),
    unconnected: (response.unconnected ?? []).map(normalizeCarrier),
    unknown: (response.unknown ?? []).map(normalizeCarrier),
    centroSelectCarrierEnabled: response.centroSelectCarrierEnabled,
  };
}

type QueryKey = ["rfp", "connected-carriers", string];

interface UseRfpConnectedCarriersOptions<TSelected> {
  rfpId: string;
  enabled?: boolean;
  queryKey?: QueryKey;
  select?: (data: RFPConnectedCarriersResponse) => TSelected;
  onSuccess?: (data: RFPConnectedCarriersResponse) => void;
}

// Returns the React Query key for RFP connected carriers. Use this to reference in cache
const getRfpConnectedCarriersQueryKey = (rfpId: string): QueryKey => [
  "rfp",
  "connected-carriers",
  rfpId,
];

// Hook to fetch RFP connected carriers given an RFP ID
// Pass in a React Query select to transform the data when reading from the cache (the original data is cached; select is applied on read)
function useRfpConnectedCarriers<TSelected = RFPConnectedCarriersResponse>({
  rfpId,
  enabled = true,
  queryKey,
  select,
  onSuccess,
}: UseRfpConnectedCarriersOptions<TSelected>): UseQueryResult<
  TSelected,
  Error
> {
  const axios = useApi();
  return useQuery<RFPConnectedCarriersResponse, Error, TSelected>({
    queryKey: queryKey ?? getRfpConnectedCarriersQueryKey(rfpId),
    enabled: enabled && !!rfpId,
    select,
    queryFn: async () => {
      const response = await axios.get<RawRFPConnectedCarriersResponse>(
        `brokerportal/v1/rfp/${rfpId}/connected-carriers`,
      );
      const data = normalizeConnectedCarriersResponse(response.data);

      onSuccess?.(data);

      return data;
    },
  });
}

export {
  getRfpConnectedCarriersQueryKey,
  useRfpConnectedCarriers,
  type RFPConnectedCarriersResponse,
};
